<template>
    <router-view></router-view>
    <TTDeveloperDebugPanel v-if="developer_panel_visible" @close="hideDeveloperPanel" />
</template>
<script>
import { defineAsyncComponent } from 'vue';

/*
 * The developer panel is hidden unless a developer explicitly enables it through
 * Debug's persisted visibility setting. Keep its inspection controls and widget
 * graph out of the login entry; Vue loads the same component before rendering the
 * panel when that opt-in state becomes visible.
 */
const TTDeveloperDebugPanel = defineAsyncComponent( () => import( '@/components/debug/TTDeveloperDebugPanel.vue' ) );

export default {
    created() {
        this.syncDeveloperPanelVisible();
        window.addEventListener( window.Debug?.developer_panel_visibility_event || 'tt-debug-panel-visibility-change', this.onDeveloperPanelVisibilityChange );
    },
    beforeUnmount() {
        window.removeEventListener( window.Debug?.developer_panel_visibility_event || 'tt-debug-panel-visibility-change', this.onDeveloperPanelVisibilityChange );
    },
    data() {
        return {
            developer_panel_visible: false,
        };
    },
    methods: {
        syncDeveloperPanelVisible() {
            this.developer_panel_visible = window.Debug?.getDeveloperPanelVisible?.() === true;
        },
        onDeveloperPanelVisibilityChange( event ) {
            this.developer_panel_visible = event?.detail?.visible === true;
        },
        hideDeveloperPanel() {
            this.developer_panel_visible = false;
        },
    },
    computed: {
    },
    components: {
        TTDeveloperDebugPanel
    }
};
</script>
<style>
@layer tt, tt-legacy;
html {
    font-size: 16px; /* Setting the root size for rem units. Previously not set and we want to set it in case a browser uses a different default. */
}

.tt-component {
    box-sizing: border-box;
    font-family: var(--tt-widget-font-family, Source Sans Pro, Arial, sans-serif);
}

.tt-link {
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    font-family: inherit;
    padding: 0;
    text-decoration: none;
}

.tt-link:disabled {
    cursor: default;
    opacity: 0.6;
}

.tt-text-secondary {
    color: rgb(from var(--tt-color-inverse-text) r g b / 0.72);
}

.tt-mr-4 {
    margin-right: 1rem;
}

.tt-tooltip {
    /* Issue #3894: Theme the neutral DOM surface; inverse foregrounds and state fills keep their own roles. */
    margin: 8px;
    max-height: calc( 100vh - 24px );
    max-width: calc( 100vw - 24px );
    pointer-events: none;
    position: fixed;
    position-anchor: var( --tt-tooltip-anchor );
    /* Anchored auto-sized boxes use the currently tried area's available width as their shrink-to-fit width. That
       lets a narrow preferred side wrap the message until it technically fits, so the browser never tries a roomier
       fallback (notably Pay Stub Amount fields, which should move an intact message from right to left). Resolve the
       intrinsic width first, capped by the shared viewport/validation limits, so overflow testing can reject the
       cramped side. The winning side still wraps genuinely long content at max-width; no JS measurement is needed. */
    width: max-content;
    --tt-tooltip-background: var(--tt-color-surface);
    --tt-tooltip-border-color: var(--tt-color-legacy-text);
    /* Field tooltips stay above 1000-level authentication surfaces, but below the
       1050+ expanded picker panels so validation text never covers selectable rows. */
    z-index: 1040;
}

.tt-tooltip-text {
    /* Issue #3894: Reading text and visible edges theme independently of fixed mask/shadow endpoints and navigation fills. */
    border: 1px solid var( --tt-tooltip-border-color );
    background: var( --tt-tooltip-background );
    color: var(--tt-color-legacy-text);
    max-height: inherit;
    max-width: inherit;
    position: relative;
    z-index: 1;
}

/* Anchor Positioning Level 1 cannot expose the winning fallback to descendant selectors. Two concentric fixed
   diamonds avoid that dependency by querying the trigger center and the already-positioned tooltip bounds. Clamping
   that center between the bounds selects the nearest point on the actual winning edge, whether CSS chose top, right,
   bottom, or left. The bordered diamond stays behind the text panel so only its outward half forms the arrow. The
   borderless diamond sits above the panel with the same background and erases the panel's 1px border across the arrow
   join; without that second layer, Pay Stub's left-side validation arrow has a conspicuous vertical line at its base.
   It must be a sibling rather than a pseudo-element because a descendant cannot escape the bordered diamond's lower
   stacking context to cover the z-index:1 panel border.

   Both diamonds must be fixed, not absolute: an absolute descendant cannot use the external trigger as an acceptable
   anchor for its tooltip-local containing box. Although each anchor() call explicitly names its source,
   position-anchor must also associate these separately positioned layers with the trigger. That association applies
   anchor scroll adjustment, so the trigger, tooltip, and complete arrow continue moving together inside an overflow
   container without JS measurements or the not-yet-cross-browser Level 2 fallback query. */
.tt-tooltip-arrow,
.tt-tooltip-arrow-mask {
    background: var( --tt-tooltip-background );
    height: 10px;
    left: clamp(
        anchor( var( --tt-tooltip-box-anchor ) left ),
        anchor( var( --tt-tooltip-anchor ) center ),
        anchor( var( --tt-tooltip-box-anchor ) right )
    );
    pointer-events: none;
    position: fixed;
    position-anchor: var( --tt-tooltip-anchor );
    top: clamp(
        anchor( var( --tt-tooltip-box-anchor ) top ),
        anchor( var( --tt-tooltip-anchor ) center ),
        anchor( var( --tt-tooltip-box-anchor ) bottom )
    );
    transform: translate( -50%, -50% ) rotate( 45deg );
    width: 10px;
}

.tt-tooltip-arrow {
    border: 1px solid var( --tt-tooltip-border-color );
    z-index: 0;
}

.tt-tooltip-arrow-mask {
    z-index: 2;
}

/* Compact topbar labels always open below a fixed-height header, so they do not need the validation tooltip's
   second tooltip-box anchor or seam mask. Safari 26.0-26.4 can resolve that chained pair to opposite panel corners;
   anchoring one dark diamond directly to the action keeps the arrow intact without changing form-error fallbacks. */
.topbar-tooltip .tt-tooltip-arrow {
    left: anchor( var( --tt-tooltip-anchor ) center );
    top: calc( anchor( var( --tt-tooltip-anchor ) bottom ) + 8px );
}

.topbar-tooltip .tt-tooltip-arrow-mask {
    display: none;
}

.tt-tooltip-top {
    position-area: top;
}

.tt-tooltip-right {
    position-area: right;
}

.tt-tooltip-bottom {
    position-area: bottom;
}

.tt-tooltip-left {
    position-area: left;
}

/* Named options let the directive preserve each caller's authored preference order while the browser, rather than
   JavaScript, rejects overflowing choices. Sizing remains on the base tooltip so every tried side has the same
   viewport-bounded, scrollable content contract. */
@position-try --tt-tooltip-top {
    position-area: top;
}

@position-try --tt-tooltip-right {
    position-area: right;
}

@position-try --tt-tooltip-bottom {
    position-area: bottom;
}

@position-try --tt-tooltip-left {
    position-area: left;
}

.tt-overlay-badge {
    display: inline-flex;
    position: relative;
}

.tt-badge {
    align-items: center;
    background: var(--tt-color-accent);
    border-radius: 999px;
    box-sizing: border-box; /* Keep one-digit badges circular; padding should only widen counts whose text cannot fit the 1.5rem minimum. */
    color: var(--tt-color-inverse-text);
    display: inline-flex;
    font-family: var(--ttfont-family, Arial, Helvetica, sans-serif); /* Badge directives can render inside tticon hosts, so text must not inherit the icon glyph font. */
    font-size: 0.75rem;
    font-weight: 700;
    height: 1.5rem;
    justify-content: center;
    line-height: 1.5rem;
    min-width: 1.5rem;
    padding: 0 0.35rem;
    position: absolute;
    right: -0.6rem;
    top: -0.6rem;
}

.tt-badge-info {
    background-color: var(--tt-color-info);
}
</style>
